import { useCallback, useEffect, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { getAreaList, getMarketGetAllMainRegions } from "tm-shared/api";
import { PointType } from "tm-shared/types";
import { validationMessage } from "tm-shared/utils";

import { useAsyncOptions } from "@/hooks";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { RHFAutocomplete } from "@/shared/forms/RHF/RHFAutocomplete";
import { setPointCreatePageOptionsCache } from "@/store/slices/ui";
import { NewPointFormValues } from "@/types/form";

import { FieldName } from "../CreatePointForm.constants";

interface LocationSectionProps {
    activeMap: Record<FieldName, boolean>;
    resetBelow: (field: FieldName) => void;
}

export const LocationSection = ({
    activeMap,
    resetBelow,
}: LocationSectionProps) => {
    // Store data
    const dispatch = useAppDispatch();
    const { optionCache } = useAppSelector((s) => s.ui.pointCreatePage);

    // Form values
    const { control } = useFormContext<NewPointFormValues>();
    const pointType = useWatch<NewPointFormValues, "pointType">({
        name: "pointType",
        control,
    });
    const partner = useWatch<NewPointFormValues, "partner">({
        name: "partner",
        control,
    });
    const region = useWatch<NewPointFormValues, "region">({
        name: "region",
        control,
    });

    // Local state
    const [isAllRegions, setIsAllRegions] = useState(false);

    // Derived values
    const shouldFetchAllRegions =
        pointType === PointType.Chains || isAllRegions;

    // Handlers
    const handleAllRegions = useCallback(() => {
        setIsAllRegions(true);
    }, []);

    // Effects
    useEffect(() => {
        setIsAllRegions(false);

        dispatch(
            setPointCreatePageOptionsCache({
                fieldName: "region",
                options: [],
            }),
        );
    }, [dispatch, partner, pointType]);

    const { options: fetchedOptions, loading: loadingRegions } =
        useAsyncOptions({
            fetchFn: (signal) => {
                if (shouldFetchAllRegions) {
                    return getAreaList();
                }

                if (!partner) {
                    throw new Error("partnerId is required");
                }

                return getMarketGetAllMainRegions(
                    {
                        partnerId: partner,
                    },
                    {
                        axiosOverrides: { signal },
                    },
                );
            },
            selectData: (response) => Object.values(response.result),
            mapToOption: (c) => ({ label: c.name, value: c.id }),
            dependencies: [partner, pointType, shouldFetchAllRegions],
            enabled: activeMap.region && (!region || shouldFetchAllRegions),
            debounceMs: 500,
        });

    useEffect(() => {
        if (fetchedOptions?.length) {
            dispatch(
                setPointCreatePageOptionsCache({
                    fieldName: "region",
                    options: fetchedOptions,
                }),
            );
        }
    }, [dispatch, fetchedOptions]);

    // Render
    return (
        <RHFAutocomplete
            name="region"
            options={optionCache.region ?? []}
            label="Регіон"
            disabled={!activeMap.region}
            onAfterChange={() => resetBelow("region")}
            allowClear={false}
            rules={{
                required: validationMessage.required,
            }}
            loading={loadingRegions}
            actionOption={
                pointType === PointType.Distribution && !isAllRegions
                    ? {
                          label: "Всі регіони",
                          onClick: handleAllRegions,
                      }
                    : undefined
            }
            selectProps={{ size: "large" }}
        />
    );
};
